import { useState } from "react";
import type { BlogStatus } from "./StatusPill";
import { formatShortDate } from "../date-utils";

const PUBLISHED_PALETTE = [
  { tile: "bg-tide-s", grad: "from-tide/30 to-tide/10", text: "text-tide" },
  { tile: "bg-spark-s", grad: "from-spark/30 to-spark/10", text: "text-spark" },
  { tile: "bg-sage-s", grad: "from-sage/30 to-sage/10", text: "text-sage" },
] as const;

function hashIndex(seed: string, mod: number): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % mod;
}

interface Props {
  status: BlogStatus;
  /** publishAt for published/scheduled posts. Ignored for drafts. */
  publishAt: string | null;
  /** CDN URL of the post's featured image, if one is set. */
  featuredImageUrl: string | null;
  /** Used to pick a stable color for the date-tile fallback. */
  seed: string;
}

/**
 * 80x56 row thumbnail. When the post has a featured image we render
 * it edge-to-edge; otherwise we fall back to the colored date-tile
 * (or a neutral "Draft" tile for unpublished posts) so every row
 * keeps a stable left-side anchor.
 */
export function BlogPostThumbnail({
  status,
  publishAt,
  featuredImageUrl,
  seed,
}: Props) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = !!featuredImageUrl && !imgFailed;

  if (showImage) {
    return (
      <div className="hair relative h-14 w-20 flex-shrink-0 overflow-hidden rounded border bg-canvas">
        <img
          src={featuredImageUrl!}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setImgFailed(true)}
          className="h-full w-full object-cover"
        />
        {status === "draft" && (
          <span className="absolute bottom-1 left-1 rounded bg-ink/85 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-paper-hi">
            Draft
          </span>
        )}
      </div>
    );
  }

  if (status === "draft" || !publishAt) {
    return (
      <div className="relative flex h-14 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-canvas-2">
        <span className="font-display relative text-[11px] font-medium text-ink-4">
          Draft
        </span>
      </div>
    );
  }

  const palette = PUBLISHED_PALETTE[hashIndex(seed, PUBLISHED_PALETTE.length)];
  return (
    <div
      className={`relative flex h-14 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded ${palette.tile}`}
    >
      <div
        className={`absolute inset-0 bg-gradient-to-br ${palette.grad}`}
        aria-hidden
      />
      <span
        className={`font-display relative text-[11px] font-medium ${palette.text}`}
      >
        {formatShortDate(publishAt)}
      </span>
    </div>
  );
}
